import { useEffect, useState, useRef } from "react";

/**
 * Detect new service worker versions and expose an `applyUpdate()` action
 * that activates the waiting SW + reloads the page.
 *
 * This is the "Update available — refresh now?" banner driver. Works for any
 * deploy (frontend code change → new SW hash → browser downloads → enters
 * waiting state). The banner doesn't auto-refresh — user clicks when ready
 * so we never interrupt them mid-form.
 *
 * Combined with useFormDraft, this means a deploy + refresh loses zero data:
 *   - Form draft auto-saves to localStorage
 *   - User clicks refresh
 *   - Page reloads with new code
 *   - Form rehydrates from the draft
 *
 * Usage (mount once at the app root):
 *   const { updateReady, applyUpdate } = useServiceWorkerUpdate();
 *   {updateReady && <UpdateAvailableBanner onRefresh={applyUpdate} />}
 *
 * Polling: every 5 minutes while the tab is open. The browser also polls
 * on its own schedule; we explicitly poll so we catch updates within minutes
 * of a deploy instead of hours.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const SW_URL = "/sw.js";

export const useServiceWorkerUpdate = () => {
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  // Only true once the user explicitly clicks "Refresh now" (applyUpdate).
  // Guards against the spurious reload that used to happen on a visitor's
  // FIRST load: the SW's activate → clients.claim() fires `controllerchange`
  // even though no update was requested, which reloaded the page ~1s after it
  // finished loading. We must reload ONLY for a user-initiated SKIP_WAITING.
  const skipWaitingRequested = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    let pollTimer = null;
    let registrationRef = null;
    let cancelled = false;

    const trackWaiting = (registration) => {
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setUpdateReady(true);
      }
    };

    const watchInstalling = (registration) => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // When a new SW finishes installing and an old one is still
        // controlling the page, the new one enters "installed" + waiting state.
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          setWaitingWorker(registration.waiting || installing);
          setUpdateReady(true);
        }
      });
    };

    const register = async () => {
      try {
        const registration = await navigator.serviceWorker.register(SW_URL);
        if (cancelled) return;
        registrationRef = registration;
        trackWaiting(registration);
        watchInstalling(registration);
        registration.addEventListener("updatefound", () => watchInstalling(registration));

        // Periodic poll: ask the browser to re-check the SW script. If the
        // server has a new version, this triggers the install → waiting flow
        // that fires our listeners above.
        pollTimer = setInterval(() => {
          if (registration && !cancelled) {
            registration.update().catch(() => {});
          }
        }, POLL_INTERVAL_MS);
      } catch (e) {
        // SW registration failed (e.g. preview HTTP, sandboxed iframe).
        // Banner just stays hidden — non-fatal.
        // eslint-disable-next-line no-console
        console.warn("[useServiceWorkerUpdate] register failed:", e);
      }
    };

    // After the new SW takes over, reload so the page is on the new code.
    // This must fire ONLY after the user clicked "Refresh now" (we sent
    // SKIP_WAITING). The browser also fires `controllerchange` on a first-ever
    // load when the freshly-installed SW calls clients.claim() — reloading then
    // is the "landing page reloads a second after it loads" bug, so we ignore
    // any controllerchange we didn't explicitly trigger.
    let reloadingForUpdate = false;
    const onControllerChange = () => {
      if (!skipWaitingRequested.current) return;
      if (reloadingForUpdate) return;
      reloadingForUpdate = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    register();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    // Mark this as a user-initiated update so onControllerChange knows the
    // upcoming controllerchange is a real refresh (and should reload).
    skipWaitingRequested.current = true;
    if (!waitingWorker) {
      // Fallback: if for some reason we lost the waiting reference, just
      // reload — the new SW will activate on the next navigation anyway.
      window.location.reload();
      return;
    }
    // Tell the new SW to take over. It calls self.skipWaiting() →
    // controllerchange fires → we reload via the listener above.
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  };

  return { updateReady, applyUpdate };
};

export default useServiceWorkerUpdate;
